import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { 
  Paper, 
  Text, 
  Container, 
  Grid, 
  Button, 
  Image, 
  Badge, 
  Tabs,
  Group,
  Modal,
  ActionIcon,
  Flex,
  Box,
  Stack,
  Title,
  Textarea,
  Center,
  UnstyledButton,
  TextInput,
  Accordion,
  FileButton,
  FileInput,
  Divider,
  NumberInput,
  Select,
  SegmentedControl,
  Switch
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconFile, IconFileText, IconPhoto, IconPdf, IconFolder, IconDownload, IconEye, IconTrash, IconEdit, IconPlus, IconX, IconMap, IconUsers, IconInfoCircle, IconShoppingCart, IconCheck, IconAlertCircle, IconCopy, IconLink, IconHelp } from '@tabler/icons-react';
import { getUserFullDetails } from '../Redux/user/slice';
import { DropzoneButton } from './DropzoneButton';
import { IntlProvider, FormattedMessage } from "react-intl";
import { BASE_URL } from '../Redux/apiConfig';
import { useIntl } from "react-intl";
import ProfilePresentation from './ProfilePresentation';
import GuidedTour from './GuidedTour';

// Helper function to convert old infoGrid object format to new array format
const normalizeInfoGrid = (infoGridData) => {
  // If it's already an array, return as-is
  if (Array.isArray(infoGridData)) {
    return infoGridData;
  }
  
  // If it's an object (old format), convert to array format
  if (infoGridData && typeof infoGridData === 'object') {
    const mapping = {
      producer: 'Producer',
      locationInfo: 'Location',
      extension: 'Extension',
      mainCrop: 'Main Crop',
      experience: 'Experience',
      practices: 'Practices',
      certifications: 'Certifications',
      altitude: 'Altitude'
    };
    
    return Object.entries(mapping).map(([key, label]) => ({
      label,
      value: infoGridData[key] || ''
    }));
  }
  
  // Default empty array with all fields
  return [
    { label: 'Producer', value: '' },
    { label: 'Location', value: '' },
    { label: 'Extension', value: '' },
    { label: 'Main Crop', value: '' },
    { label: 'Experience', value: '' },
    { label: 'Practices', value: '' },
    { label: 'Certifications', value: '' },
    { label: 'Altitude', value: '' },
  ];
};

export default function MyPersonalSpace({ activeTab = 'userInfo', onTabChange, accountIdOverride = null }) {
  // User info and Redux setup
  const userinfo = JSON.parse(localStorage.getItem('userinfo'));
  const accountId = accountIdOverride || userinfo?.accountId;
  const userFullDetails = useSelector((state) => state.user.userFullDetails);
  const [usdcBalance, setUSDCBalance] = useState(0);
  const [hbarBalance, setHBARBalance] = useState(0);
  const dispatch = useDispatch();
  
  // State for copy and profile presentation
  const [copySuccess, setCopySuccess] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('idle');
  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);

  // Profile Presentation State - Basic
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [editTitleModal, setEditTitleModal] = useState(false);
  const [editSubtitleModal, setEditSubtitleModal] = useState(false);
  
  // Hero Image
  const [heroImage, setHeroImage] = useState('');
  
  // Background Image
  const [backgroundImage, setBackgroundImage] = useState('');
  
  // Description & Info Grid
  const [description, setDescription] = useState('');
  const [editDescriptionModal, setEditDescriptionModal] = useState(false);
  const [infoGrid, setInfoGrid] = useState([
    { label: 'Producer', value: '' },
    { label: 'Location', value: '' },
    { label: 'Extension', value: '' },
    { label: 'Main Crop', value: '' },
    { label: 'Experience', value: '' },
    { label: 'Practices', value: '' },
    { label: 'Certifications', value: '' },
    { label: 'Altitude', value: '' },
  ]);
  const [editInfoGridModal, setEditInfoGridModal] = useState(false);
  
  // Location Data
  const [location, setLocation] = useState({
    address: '',
    description: '',
    mapUrl: '',
    coordinates: '',
    coordinatesDMS: '',
  });
  const [editLocationModal, setEditLocationModal] = useState(false);
  
  // Gallery Images
  const [gallery, setGallery] = useState([]);
  const [editGalleryModal, setEditGalleryModal] = useState(false);
  
  // Product Lots
  const [productLots, setProductLots] = useState([]);
  const [editProductLotsModal, setEditProductLotsModal] = useState(false);
  const [editingLot, setEditingLot] = useState(null);
  const [lotCode, setLotCode] = useState('');
  const [lotName, setLotName] = useState('');
  const [lotCertUrl, setLotCertUrl] = useState('');
  
  // Contact Info
  const [contact, setContact] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
  });
  const [editContactModal, setEditContactModal] = useState(false);
  
  // Social Media Links
  const [socialLinks, setSocialLinks] = useState({
    instagram: '',
    twitter: '',
    website: '',
  });
  
  // Custom Links (shop, labels, etc.)
  const [customLinks, setCustomLinks] = useState([]);
  
  // Theme Color
  const [headerColor, setHeaderColor] = useState('brown'); // Default brown theme
  
  // Auto-save status
  const [saveStatus, setSaveStatus] = useState(''); // '', 'saving', 'saved'
  
  // Legacy documents state (keeping for backward compatibility)
  const [documents, setDocuments] = useState([]);
  
  // Edit document link state
  const [editDocumentModal, setEditDocumentModal] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  const [editDocumentURL, setEditDocumentURL] = useState('');
  const [editDocumentName, setEditDocumentName] = useState('');

  // Add to your state (top of component)
  const [files, setFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);

  // Add this state near the top with other state declarations:
  const [qrCodeUrl, setQrCodeUrl] = useState('');

  const [hasCustomTemplate, setHasCustomTemplate] = useState(false);
  const [previewMode, setPreviewMode] = useState('custom');

  const [profileLoaded, setProfileLoaded] = useState(false);

  // Publish toggle
  const [published, setPublished] = useState(false);

  // Personal Space Tour State
  const [runTour, setRunTour] = useState(false);
  const intl = useIntl();

  // Wrapper functions to save to localStorage immediately on every change
  const makeLocalSetter = (setter, key, shouldStringify = false) => (value) => {
    setter(value);
    if (accountId) {
      localStorage.setItem(`profile_${key}_${accountId}`, shouldStringify ? JSON.stringify(value) : value);
    }
  };

  const setTitleWithSave        = makeLocalSetter(setTitle,        'title');
  const setSubtitleWithSave     = makeLocalSetter(setSubtitle,     'subtitle');
  const setHeroImageWithSave    = makeLocalSetter(setHeroImage,    'heroImage');
  const setDescriptionWithSave  = makeLocalSetter(setDescription,  'description');
  const setGalleryWithSave      = makeLocalSetter(setGallery,      'gallery',      true);
  const setInfoGridWithSave     = makeLocalSetter(setInfoGrid,     'infoGrid',     true);
  const setLocationWithSave     = makeLocalSetter(setLocation,     'location',     true);
  const setProductLotsWithSave  = makeLocalSetter(setProductLots,  'productLots',  true);
  const setContactWithSave      = makeLocalSetter(setContact,      'contact',      true);
  const setSocialLinksWithSave  = makeLocalSetter(setSocialLinks,  'socialLinks',  true);
  const setCustomLinksWithSave  = makeLocalSetter(setCustomLinks,  'customLinks',  true);
  const setHeaderColorWithSave  = makeLocalSetter(setHeaderColor,  'headerColor');
  const setDocumentsWithSave    = makeLocalSetter(setDocuments,    'documents',    true);

  // Load from localStorage on mount (runs FIRST)
  useEffect(() => {
    if (!accountId) return;
    
    const savedTitle = localStorage.getItem(`profile_title_${accountId}`);
    const savedSubtitle = localStorage.getItem(`profile_subtitle_${accountId}`);
    const savedHeroImage = localStorage.getItem(`profile_heroImage_${accountId}`);
    const savedDescription = localStorage.getItem(`profile_description_${accountId}`);
    const savedGallery = localStorage.getItem(`profile_gallery_${accountId}`);
    const savedInfoGrid = localStorage.getItem(`profile_infoGrid_${accountId}`);
    const savedLocation = localStorage.getItem(`profile_location_${accountId}`);
    const savedProductLots = localStorage.getItem(`profile_productLots_${accountId}`);
    const savedContact = localStorage.getItem(`profile_contact_${accountId}`);
    const savedSocialLinks = localStorage.getItem(`profile_socialLinks_${accountId}`);
    const savedCustomLinks = localStorage.getItem(`profile_customLinks_${accountId}`);
    const savedHeaderColor = localStorage.getItem(`profile_headerColor_${accountId}`);
    const savedDocuments = localStorage.getItem(`profile_documents_${accountId}`);
    
    if (savedTitle) setTitle(savedTitle);
    if (savedSubtitle) setSubtitle(savedSubtitle);
    if (savedHeroImage) setHeroImage(savedHeroImage);
    if (savedDescription) setDescription(savedDescription);
    if (savedGallery) setGallery(JSON.parse(savedGallery));
    if (savedInfoGrid) setInfoGrid(normalizeInfoGrid(JSON.parse(savedInfoGrid)));
    if (savedLocation) setLocation(JSON.parse(savedLocation));
    if (savedProductLots) setProductLots(JSON.parse(savedProductLots));
    if (savedContact) setContact(JSON.parse(savedContact));
    if (savedSocialLinks) setSocialLinks(JSON.parse(savedSocialLinks));
    if (savedCustomLinks) setCustomLinks(JSON.parse(savedCustomLinks));
    if (savedHeaderColor) setHeaderColor(savedHeaderColor);
    if (savedDocuments) setDocuments(JSON.parse(savedDocuments));
    const savedPublished = localStorage.getItem(`profile_published_${accountId}`);
    if (savedPublished !== null) setPublished(JSON.parse(savedPublished));
    
    // Mark as loaded if we have localStorage data
    if (savedTitle || savedSubtitle || savedHeroImage || savedDescription || savedGallery || 
        savedInfoGrid || savedLocation || savedProductLots || savedContact || savedSocialLinks || 
        savedCustomLinks || savedHeaderColor || savedDocuments) {
      setProfileLoaded(true);
    }
  }, [accountId]);

  const handleTemplateStatusChange = (status) => {
    setHasCustomTemplate(status);
  };

  // Check if ProfilePresentation.html exists
  useEffect(() => {
    const checkCustomTemplate = async () => {
      if (!accountId) return;
      
      try {
        const url = `https://vertoledgerstorage.blob.core.windows.net/sqldbledgerdigests/userSpace/${accountId}/ProfilePresentation.html`;
        // Use GET directly (HEAD may be blocked by CORS on Azure Blob)
        const response = await fetch(url);
        
        if (response.ok) {
          const html = await response.text();
          const trimmedLower = html.trim().toLowerCase();
          
          if (trimmedLower.length > 50 && (trimmedLower.includes('<html') || trimmedLower.includes('<!doctype'))) {
            setHasCustomTemplate(true);
          } else {
            setHasCustomTemplate(false);
          }
        } else {
          setHasCustomTemplate(false);
        }
      } catch (error) {
        setHasCustomTemplate(false);
      }
    };

    checkCustomTemplate();
  }, [accountId]);

  // Fetch the USDC balance
  useEffect(() => {
      const fetchUSDCBalance = async () => {
        try {
          const response = await fetch(`${BASE_URL}/api/getUSDCBalance/${accountId}`);
          if (response.ok) {
            const data = await response.json();
            setUSDCBalance(data.balance);
          } else {
            console.error('Failed to fetch USDC balance');
          }
        } catch (error) {
          console.error('Error fetching USDC balance:', error);
        }
      };

      if (userinfo?.accountId) {
        fetchUSDCBalance();
      }
    }, [userinfo?.accountId]);


  // Fetch the HBAR balance
  useEffect(() => {
      const fetchHBARBalance = async () => {
        try {
          const response = await fetch(`${BASE_URL}/api/getHBARBalance/${accountId}`);
          if (response.ok) {
            const data = await response.json();
            setHBARBalance(data.balance);
          } else {
            console.error('Failed to fetch hbar balance');
          }
        } catch (error) {
          console.error('Error fetching hbar balance:', error);
        }
      };

      if (userinfo?.accountId) {
        fetchHBARBalance();
      }
    }, [userinfo?.accountId]);

  // Add this useEffect after your existing useEffects:
  useEffect(() => {
    const fetchQRCode = async () => {
      try {
        const response = await fetch(`${BASE_URL}/api/getProfileQRCode/${accountId}`);
        if (response.ok) {
          const data = await response.json();
          setQrCodeUrl(`${data.qrCodeUrl}?t=${Date.now()}`);
        }
      } catch (error) {
        console.error('Error fetching QR code:', error);
      }
    };

    if (userinfo?.accountId) {
      fetchQRCode();
    }
  }, [userinfo?.accountId]);

  const fetchFiles = async () => {
    setLoadingFiles(true);
    try {
      const authToken = localStorage.getItem('authToken');
      
      const response = await fetch(`${BASE_URL}/api/documents/${accountId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
          'apikey': 'abc123'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setFiles(data);
      } else {
        console.error('Failed to fetch files:', response.status);
        setFiles([]);
      }
    } catch (error) {
      console.error('Error fetching files:', error);
      setFiles([]);
    }
    setLoadingFiles(false);
  };

  useEffect(() => {
    fetchFiles();
  }, []);

  // Fetch user details on component mount
  useEffect(() => {
    if (!userFullDetails.userId && userinfo?.email) {
      dispatch(getUserFullDetails({ email: userinfo.email }));
    }
  }, [dispatch, userinfo?.email, userFullDetails.userId]);

  // Add this useEffect to load saved profile data (only once on mount)
  useEffect(() => {
    const fetchProfile = async () => {
      if (profileLoaded) return; // Skip if already loaded from localStorage
      
      try {
        const response = await fetch(`${BASE_URL}/api/profile/${accountId}`);
        if (response.ok) {
          const data = await response.json();
          // Only update if localStorage doesn't have newer data
          if (!localStorage.getItem(`profile_title_${accountId}`)) {
            setTitle(data.title || '');
          }
          if (!localStorage.getItem(`profile_subtitle_${accountId}`)) {
            setSubtitle(data.subtitle || '');
          }
          if (!localStorage.getItem(`profile_heroImage_${accountId}`)) {
            setHeroImage(data.heroImage || '');
          }
          if (!localStorage.getItem(`profile_description_${accountId}`)) {
            setDescription(data.description || '');
          }
          if (!localStorage.getItem(`profile_gallery_${accountId}`)) {
            setGallery(data.gallery || []);
          }
          if (!localStorage.getItem(`profile_infoGrid_${accountId}`)) {
            setInfoGrid(normalizeInfoGrid(data.infoGrid));
          }
          if (!localStorage.getItem(`profile_location_${accountId}`)) {
            setLocation(data.location || {
              address: '',
              description: '',
              mapUrl: '',
              coordinates: '',
              coordinatesDMS: '',
            });
          }
          if (!localStorage.getItem(`profile_productLots_${accountId}`)) {
            setProductLots(data.productLots || []);
          }
          if (!localStorage.getItem(`profile_contact_${accountId}`)) {
            setContact(data.contact || {
              name: '',
              phone: '',
              email: '',
              address: '',
            });
          }
          if (!localStorage.getItem(`profile_socialLinks_${accountId}`)) {
            setSocialLinks(data.socialLinks || {
              instagram: '',
              twitter: '',
              website: '',
            });
          }
          if (!localStorage.getItem(`profile_customLinks_${accountId}`)) {
            setCustomLinks(data.customLinks || []);
          }
          if (!localStorage.getItem(`profile_headerColor_${accountId}`)) {
            setHeaderColor(data.headerColor || 'brown');
          }
          if (!localStorage.getItem(`profile_documents_${accountId}`)) {
            setDocuments(data.documents || []);
          }
          setPreviewMode(data.profileDisplayMode || 'custom');
          const serverPublished = data.published ?? true;
          setPublished(serverPublished);
          localStorage.setItem(`profile_published_${accountId}`, JSON.stringify(serverPublished));
          setProfileLoaded(true);
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
      }
    };

    if (accountId && !profileLoaded) {
      fetchProfile();
    }
  }, [accountId, profileLoaded]);

  // Copy Public Key to clipboard
  const copyPublicKey = () => {
    navigator.clipboard.writeText(userFullDetails.PublicKey);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  // Download QR Code handler
  const downloadQRCode = () => {
    const link = document.createElement('a');
    link.href = `https://vertoledgerstorage.blob.core.windows.net/sqldbledgerdigests/userSpace/${accountId}/profile_qr.png`;
    link.download = 'profile_qr.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Copy Profile Link handler
  const copyProfileLink = async () => {
    const profileUrl = `${window.location.origin}/profile/${accountId}`;
    try {
      await navigator.clipboard.writeText(profileUrl);
      notifications.show({
        title: 'Link copied!',
        message: 'Profile link copied to clipboard',
        color: 'teal',
        icon: <IconCheck size="1rem" />,
        autoClose: 3000,
      });
    } catch (error) {
      console.error('Failed to copy link:', error);
      notifications.show({
        title: 'Copy failed',
        message: 'Please copy the link manually from your browser',
        color: 'red',
        icon: <IconAlertCircle size="1rem" />,
        autoClose: 4000,
      });
    }
  };

  // Document download handler
  const handleDocumentDownload = (url, name) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDeleteFile = async (fileName) => {
    const confirmMessage = intl.formatMessage(
      { id: "files.delete.confirm" },
      { fileName: fileName }
    );
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const authToken = localStorage.getItem('authToken');
      
      const response = await fetch(`${BASE_URL}/api/documents/${accountId}/${encodeURIComponent(fileName)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'apikey': 'abc123'
        }
      });
      
      if (response.ok) {
        fetchFiles();
        notifications.show({
          title: 'File deleted',
          message: `${fileName} has been deleted`,
          color: 'green',
          icon: <IconCheck size="1rem" />,
        });
      } else {
        console.error('Failed to delete file:', response.status);
        notifications.show({
          title: 'Error',
          message: 'Failed to delete file',
          color: 'red',
          icon: <IconAlertCircle size="1rem" />,
        });
      }
    } catch (error) {
      console.error('Error deleting file:', error);
      notifications.show({
        title: 'Network Error',
        message: 'Could not delete file. Please try again.',
        color: 'red',
        icon: <IconAlertCircle size="1rem" />,
      });
    }
  };

  // Navigate to document page handler
  const navigateToDocument = (url) => {
    window.open(url, '_blank');
  };
  
  // Open edit document modal
  const openEditDocumentModal = (doc) => {
    setEditingDocument(doc);
    setEditDocumentURL(doc.url);
    setEditDocumentName(doc.name); 
    setEditDocumentModal(true);
  };
  
  // Save document changes (both name and URL)
  const saveDocumentURL = () => {
    if (editingDocument) {
      const updatedDocuments = documents.map(doc => 
        doc.id === editingDocument.id 
          ? { ...doc, name: editDocumentName, url: editDocumentURL }
          : doc
      );
      setDocumentsWithSave(updatedDocuments);
    }
    setEditDocumentModal(false);
    saveProfile();
  };
  
  // Handle publish toggle
  const handlePublishToggle = (checked) => {
    setPublished(checked);
    if (accountId) {
      localStorage.setItem(`profile_published_${accountId}`, JSON.stringify(checked));
    }
    saveProfile(undefined, checked);
  };

  // Add this function after saveDocumentURL:
  const saveProfile = async (overrideMode, overridePublished) => {
    try {
      if (!userinfo?.accountId) {
        notifications.show({
          title: 'Missing account',
          message: 'Cannot save profile without account ID.',
          color: 'red',
          icon: <IconAlertCircle size="1rem" />,
        });
        return;
      }

      const authToken = localStorage.getItem('authToken');
      
      const sanitize = (val) => {
        if (val === null || val === undefined) return val;
        if (typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean') return val;
        if (typeof val === 'function') return undefined;
        if (Array.isArray(val)) return val.map(sanitize).filter(v => v !== undefined);
        if (typeof val === 'object' && val.constructor === Object) {
          return Object.fromEntries(
            Object.entries(val)
              .map(([k, v]) => [k, sanitize(v)])
              .filter(([, v]) => v !== undefined)
          );
        }
        return undefined; // strip DOM nodes, React elements, File objects, etc.
      };

      const profileData = sanitize({ 
        title, 
        subtitle,
        heroImage,
        description,
        infoGrid,
        location,
        gallery,
        productLots,
        contact,
        socialLinks,
        customLinks,
        headerColor,
        documents,
        profileDisplayMode: overrideMode ?? previewMode,
        published: overridePublished !== undefined ? overridePublished : published
      });
      
      const response = await fetch(`${BASE_URL}/api/profile/${accountId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
          'apikey': 'abc123'
        },
        body: JSON.stringify(profileData)
      });
      
      if (response.ok) {
        const contentType = response.headers.get('content-type') || '';
        if (contentType.includes('application/json')) {
          try {
            await response.json();
          } catch (parseError) {
            console.warn('Profile saved but response JSON parsing failed:', parseError);
          }
        }
        
        const profileUrl = `${window.location.origin}/profile/${accountId}`;

        try {
          await navigator.clipboard.writeText(profileUrl);
          notifications.show({
            title: 'Profile saved!',
            message: 'Link copied to clipboard',
            color: 'teal',
            icon: <IconCheck size="1rem" />,
            autoClose: 3000,
          });
        } catch (clipboardError) {
          console.warn('Profile saved but clipboard copy failed:', clipboardError);
          notifications.show({
            title: 'Profile saved',
            message: 'Saved successfully, but link copy failed. Please copy from browser address bar.',
            color: 'yellow',
            icon: <IconAlertCircle size="1rem" />,
            autoClose: 4000,
          });
        }
      } else {
        const errorText = await response.text();
        console.error('❌ Save failed:', response.status, errorText);
        notifications.show({
          title: 'Save failed',
          message: `Could not save profile (Status: ${response.status})`,
          color: 'red',
          icon: <IconAlertCircle size="1rem" />,
        });
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      notifications.show({
        title: 'Network error',
        message: 'Could not save profile. Please check your connection.',
        color: 'red',
        icon: <IconAlertCircle size="1rem" />,
      });
    }
  };

  // Auto-save effect with debouncing
  useEffect(() => {
    if (!accountId || !title) {
      return;
    }
    
    const timer = setTimeout(() => {
      const autoSave = async () => {
        try {
          setSaveStatus('saving');
          const authToken = localStorage.getItem('authToken');
          
          const profileData = { 
            title, 
            subtitle,
            heroImage,
            description,
            infoGrid,
            location,
            gallery,
            productLots,
            contact,
            headerColor,
            documents,
            published
          };
          
          const response = await fetch(`${BASE_URL}/api/profile/${accountId}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${authToken}`,
              'apikey': 'abc123'
            },
            body: JSON.stringify(profileData)
          });
          
          if (response.ok) {
            const contentType = response.headers.get('content-type') || '';
            if (contentType.includes('application/json')) {
              try {
                await response.json();
              } catch (parseError) {
                // Ignore parse error
              }
            }
            setSaveStatus('saved');
            setTimeout(() => setSaveStatus(''), 2000);
          } else {
            const errorText = await response.text();
            console.error('❌ AUTO-SAVE failed:', response.status, errorText);
            setSaveStatus('');
          }
        } catch (error) {
          console.error('❌ AUTO-SAVE error:', error);
          setSaveStatus('');
        }
      };
      
      autoSave();
    }, 800); // Auto-save after 800ms of no changes
    
    return () => clearTimeout(timer);
  }, [title, subtitle, heroImage, description, infoGrid, location, gallery, productLots, contact, headerColor, documents, published, accountId]);

  // Load background image URL on mount
  useEffect(() => {
    if (accountId) {
      const bgUrl = `https://vertoledgerstorage.blob.core.windows.net/sqldbledgerdigests/userSpace/${accountId}/background.jpg?t=${Date.now()}`;
      setBackgroundImage(bgUrl);
    }
  }, [accountId]);

  // Add this function with your other handlers:
  const handleProfileImageUpload = async (file) => {
    try {
      const authToken = localStorage.getItem('authToken');
      const reader = new FileReader();
      
      reader.onloadend = async () => {
        const base64Data = reader.result.split(',')[1];
        
        const response = await fetch(`${BASE_URL}/api/upload-document`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`,
            'apikey': 'abc123'
          },
          body: JSON.stringify({
            accountId: accountId,
            fileName: 'p.png',
            fileData: base64Data,
            fileType: file.type
          })
        });
        
        if (response.ok) {
          notifications.show({
            title: 'Success!',
            message: 'Profile image updated. Refreshing page...',
            color: 'teal',
            icon: <IconCheck size="1rem" />,
          });
          setTimeout(() => window.location.reload(), 1000);
        } else {
          notifications.show({
            title: 'Upload failed',
            message: 'Could not upload profile image',
            color: 'red',
            icon: <IconAlertCircle size="1rem" />,
          });
        }
      };
      
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error uploading profile image:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to upload image',
        color: 'red',
        icon: <IconAlertCircle size="1rem" />,
      });
    }
  };

  // Add this function with your other handlers:
  const handleBackgroundImageUpload = async (file) => {
    if (!file) return;
    
    try {
      const authToken = localStorage.getItem('authToken');
      const reader = new FileReader();
      
      reader.onloadend = async () => {
        const base64Data = reader.result.split(',')[1];
        
        const response = await fetch(`${BASE_URL}/api/upload-document`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`,
            'apikey': 'abc123'
          },
          body: JSON.stringify({
            accountId: accountId,
            fileName: 'background.jpg',
            fileData: base64Data,
            fileType: file.type
          })
        });
        
        if (response.ok) {
          // Update the background image URL in state
          const newBackgroundUrl = `https://vertoledgerstorage.blob.core.windows.net/sqldbledgerdigests/userSpace/${accountId}/background.jpg?t=${Date.now()}`;
          setBackgroundImage(newBackgroundUrl);
          
          notifications.show({
            title: 'Background updated!',
            message: 'Your background image has been saved successfully',
            color: 'teal',
            icon: <IconCheck size="1rem" />,
          });
        } else {
          notifications.show({
            title: 'Upload failed',
            message: 'Could not upload background image',
            color: 'red',
            icon: <IconAlertCircle size="1rem" />,
          });
        }
      };
      
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error uploading background image:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to upload background',
        color: 'red',
        icon: <IconAlertCircle size="1rem" />,
      });
    }
  };

  const handleHeroImageUpload = async (file) => {
    if (!file) return;
    
    try {
      const authToken = localStorage.getItem('authToken');
      const reader = new FileReader();
      
      reader.onloadend = async () => {
        const base64Data = reader.result.split(',')[1];
        
        const response = await fetch(`${BASE_URL}/api/upload-document`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`,
            'apikey': 'abc123'
          },
          body: JSON.stringify({
            accountId: accountId,
            fileName: `hero_${Date.now()}.jpg`,
            fileData: base64Data,
            fileType: file.type
          })
        });
        
        if (response.ok) {
          const data = await response.json();
          setHeroImageWithSave(data.url);
          notifications.show({
            title: 'Hero image uploaded!',
            message: 'Image URL has been set',
            color: 'teal',
            icon: <IconCheck size="1rem" />,
          });
        } else {
          notifications.show({
            title: 'Upload failed',
            message: 'Could not upload hero image',
            color: 'red',
            icon: <IconAlertCircle size="1rem" />,
          });
        }
      };
      
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error uploading hero image:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to upload hero image',
        color: 'red',
        icon: <IconAlertCircle size="1rem" />,
      });
    }
  };

  const handleGalleryImageUpload = async (file) => {
    if (!file) return;
    
    try {
      const authToken = localStorage.getItem('authToken');
      const reader = new FileReader();
      
      reader.onloadend = async () => {
        const base64Data = reader.result.split(',')[1];
        
        const response = await fetch(`${BASE_URL}/api/upload-document`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`,
            'apikey': 'abc123'
          },
          body: JSON.stringify({
            accountId: accountId,
            fileName: `gallery_${Date.now()}.jpg`,
            fileData: base64Data,
            fileType: file.type
          })
        });
        
        if (response.ok) {
          const data = await response.json();
          const updatedGallery = [...gallery, data.url];
          setGalleryWithSave(updatedGallery);
          notifications.show({
            title: 'Gallery image uploaded!',
            message: 'Image added to gallery',
            color: 'teal',
            icon: <IconCheck size="1rem" />,
          });
        } else {
          notifications.show({
            title: 'Upload failed',
            message: 'Could not upload gallery image',
            color: 'red',
            icon: <IconAlertCircle size="1rem" />,
          });
        }
      };
      
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error uploading gallery image:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to upload gallery image',
        color: 'red',
        icon: <IconAlertCircle size="1rem" />,
      });
    }
  };

  // Personal Space Tour Steps - Conditional based on active tab
  const getPersonalSpaceTourSteps = () => {
    if (activeTab === 'profilePresentation') {
      return [
        {
          target: 'body',
          content: intl.formatMessage({ id: 'tour.profilepresentation.welcome.content' }),
          title: intl.formatMessage({ id: 'tour.profilepresentation.welcome.title' }),
          placement: 'center',
          disableBeacon: true,
        },
        {
          target: '[data-tour-id="pp-qr-save"]',
          content: intl.formatMessage({ id: 'tour.profilepresentation.qrsave.content' }),
          title: intl.formatMessage({ id: 'tour.profilepresentation.qrsave.title' }),
          placement: 'right',
          disableBeacon: true,
        },
        {
          target: '[data-tour-id="pp-edit-sections"]',
          content: intl.formatMessage({ id: 'tour.profilepresentation.edit.content' }),
          title: intl.formatMessage({ id: 'tour.profilepresentation.edit.title' }),
          placement: 'right',
          disableBeacon: true,
        },
        {
          target: '[data-tour-id="pp-preview"]',
          content: intl.formatMessage({ id: 'tour.profilepresentation.preview.content' }),
          title: intl.formatMessage({ id: 'tour.profilepresentation.preview.title' }),
          placement: 'left',
          disableBeacon: true,
        },
        {
          target: 'body',
          content: intl.formatMessage({ id: 'tour.profilepresentation.end.content' }),
          title: intl.formatMessage({ id: 'tour.profilepresentation.end.title' }),
          placement: 'center',
          disableBeacon: true,
        },
      ];
    } else if (activeTab === 'files') {
      return [
        {
          target: 'body',
          content: intl.formatMessage({ id: 'tour.files.welcome.content' }),
          title: intl.formatMessage({ id: 'tour.files.welcome.title' }),
          placement: 'center',
          disableBeacon: true,
        },
        {
          target: '[data-tour-id="files-upload"]',
          content: intl.formatMessage({ id: 'tour.files.upload.content' }),
          title: intl.formatMessage({ id: 'tour.files.upload.title' }),
          placement: 'bottom',
          disableBeacon: true,
        },
        {
          target: '[data-tour-id="files-list"]',
          content: intl.formatMessage({ id: 'tour.files.list.content' }),
          title: intl.formatMessage({ id: 'tour.files.list.title' }),
          placement: 'top',
          disableBeacon: true,
        },
        {
          target: 'body',
          content: intl.formatMessage({ id: 'tour.files.end.content' }),
          title: intl.formatMessage({ id: 'tour.files.end.title' }),
          placement: 'center',
          disableBeacon: true,
        },
      ];
    } else {
      // userInfo tab
      return [
        {
          target: 'body',
          content: intl.formatMessage({ id: 'tour.personalspace.welcome.content' }),
          title: intl.formatMessage({ id: 'tour.personalspace.welcome.title' }),
          placement: 'center',
          disableBeacon: true,
        },
        {
          target: '[data-tour-id="ps-user-info"]',
          content: intl.formatMessage({ id: 'tour.personalspace.userinfo.content' }),
          title: intl.formatMessage({ id: 'tour.personalspace.userinfo.title' }),
          placement: 'bottom',
          disableBeacon: true,
        },
        {
          target: '[data-tour-id="ps-profile-details"]',
          content: intl.formatMessage({ id: 'tour.personalspace.profile.content' }),
          title: intl.formatMessage({ id: 'tour.personalspace.profile.title' }),
          placement: 'bottom',
          disableBeacon: true,
        },
        {
          target: '[data-tour-id="ps-hedera-wallet"]',
          content: intl.formatMessage({ id: 'tour.personalspace.wallet.content' }),
          title: intl.formatMessage({ id: 'tour.personalspace.wallet.title' }),
          placement: 'bottom',
          disableBeacon: true,
        },
        {
          target: 'body',
          content: intl.formatMessage({ id: 'tour.personalspace.end.content' }),
          title: intl.formatMessage({ id: 'tour.personalspace.end.title' }),
          placement: 'center',
          disableBeacon: true,
        },
      ];
    }
  };

  const personalSpaceTourSteps = getPersonalSpaceTourSteps();

  // Guard clause for null userinfo
  if (!userinfo) {
    return (
      <Container size="xl">
        <Paper padding="md" shadow="xs">
          <Center style={{ minHeight: '50vh' }}>
            <Text>Please log in to view your personal space</Text>
          </Center>
        </Paper>
      </Container>
    );
  }

  return (
    <Box p="md">
        {/* Personal Space Header with Tour Button */}
        <Group position="apart" mb="lg">
          <Title order={2}>
            <FormattedMessage id="mylist.navbar.userSettings" defaultMessage="Personal Space" />
          </Title>
          <ActionIcon
            size="lg"
            variant="light"
            color="blue"
            onClick={() => setRunTour(true)}
            title={intl.formatMessage({ id: 'tour.button.start' })}
          >
            <IconHelp size="1.2rem" />
          </ActionIcon>
        </Group>

        <Tabs value={activeTab}>
          {/* User Information Tab */}
          <Tabs.Panel value="userInfo" pt="xs">
            <Container size="lg" py="xl">
              {/* Profile Header Card */}
              <Paper 
                withBorder 
                p="xl" 
                radius="md" 
                mb="xl"
                style={{ backgroundColor: 'rgba(249, 250, 251, 0.5)' }}
                data-tour-id="ps-user-info"
              >
                <Grid>
                  <Grid.Col span={12} md={4}>
                    <Center>
                      <Box style={{ position: 'relative' }}>
                        <Image
                          src={`https://vertoledgerstorage.blob.core.windows.net/sqldbledgerdigests/userSpace/${accountId}/p.png?t=${Date.now()}`}
                          fallbackSrc="https://avatar.iran.liara.run/public/boy"
                          alt="User Profile Image"
                          width={160}
                          height={160}
                          radius="md"
                          fit="cover"
                          withPlaceholder
                        />
                        
                        <label htmlFor="profile-upload" style={{ 
                          position: 'absolute', 
                          bottom: '5px', 
                          right: '5px',
                          cursor: 'pointer'
                        }}>
                          <ActionIcon 
                            size="md" 
                            color="blue" 
                            variant="filled"
                            component="span"
                            radius="xl"
                            style={{ boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}
                          >
                            <IconEdit size="1rem" />
                          </ActionIcon>
                        </label>
                        
                        <input
                          id="profile-upload"
                          type="file"
                          accept="image/*"
                          style={{ display: 'none' }}
                          onChange={(e) => {
                            if (e.target.files?.[0]) {
                              handleProfileImageUpload(e.target.files[0]);
                            }
                          }}
                        />
                      </Box>
                    </Center>
                  </Grid.Col>

                  <Grid.Col span={12} md={8}>
                    <Stack spacing="xs">
                      <Title order={3} weight={600}>
                        {userFullDetails?.userName || 'User'}
                      </Title>
                      <Text size="sm" color="dimmed">
                        {userFullDetails?.email || 'No email'}
                      </Text>
                      <Badge 
                        size="lg" 
                        variant="light" 
                        color="blue"
                        leftSection={<Text size="xs">🌍</Text>}
                      >
                        {userFullDetails?.Country || 'Unknown'}
                      </Badge>
                    </Stack>
                  </Grid.Col>
                </Grid>
              </Paper>

              {/* Account Details */}
              <Paper withBorder p="md" radius="md" mb="md" data-tour-id="ps-profile-details">
                <Title order={5} mb="md" weight={500} color="dimmed">
                  <FormattedMessage id="userInfo.acctID" defaultMessage="Account Information" />
                </Title>
                
                <Stack spacing="sm">
                  <Group position="apart" style={{ padding: '8px 0', borderBottom: '1px solid #f1f3f5' }}>
                    <Text size="sm" color="dimmed">
                      <FormattedMessage id="userInfo.acctID.profile" />
                    </Text>
                    <Text size="sm" weight={500}>{userFullDetails?.accountId || 'N/A'}</Text>
                  </Group>

                  <Group position="apart" style={{ padding: '8px 0', borderBottom: '1px solid #f1f3f5' }}>
                    <Text size="sm" color="dimmed">
                      <FormattedMessage id="userInfo.prodcontractID" />
                    </Text>
                    <Text size="sm" weight={500}>{userFullDetails?.product_contractId || 'N/A'}</Text>
                  </Group>

                  <Group position="apart" style={{ padding: '8px 0', borderBottom: '1px solid #f1f3f5' }}>
                    <Text size="sm" color="dimmed">
                      <FormattedMessage id="userInfo.comercontractID" />
                    </Text>
                    <Text size="sm" weight={500}>{userFullDetails?.commercial_contractId || 'N/A'}</Text>
                  </Group>
                </Stack>
              </Paper>

              {/* Blockchain Details */}
              <Paper withBorder p="md" radius="md" mb="md" data-tour-id="ps-hedera-wallet">
                <Title order={5} mb="md" weight={500} color="dimmed">
                  Blockchain Information
                </Title>
                
                <Stack spacing="sm">
                  <Box style={{ padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                    <Group position="apart" mb="xs">
                      <Text size="sm" color="dimmed">
                        <FormattedMessage id="userInfo.hederaAddress" />
                      </Text>
                      <Button
                        component="a"
                        href={`https://hashscan.io/${userFullDetails?.userName?.includes('#') ? 'testnet' : 'mainnet'}/account/${userFullDetails?.accountId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="xs"
                        variant="light"
                        compact
                      >
                        HashScan →
                      </Button>
                    </Group>
                    <Text size="sm" weight={500}>{userFullDetails?.accountId || 'N/A'}</Text>
                  </Box>

                  <Box style={{ padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                    <Group position="apart" mb="xs">
                      <Text size="sm" color="dimmed">
                        <FormattedMessage id="userInfo.balance" />
                      </Text>
                    </Group>
                    <Group spacing="xs">
                      <Text size="lg" weight={600}>{hbarBalance}</Text>
                      <Badge size="lg" color="green" variant="light">ℏ</Badge>
                    </Group>

                  </Box>
                   <Box style={{ padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                    <Group position="apart" mb="xs">
                      <Text size="sm" color="dimmed">
                        <FormattedMessage id="userInfo.usdcBalance" defaultMessage="USDC Balance" />
                      </Text>
                    </Group>
                    <Group spacing="xs">
                      <Text size="lg" weight={600}>{usdcBalance}</Text>
                      <Badge size="lg" color="green" variant="light">USDC</Badge>
                    </Group>
                  </Box>


                  <Box style={{ padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                    <Group position="apart" mb="xs">
                      <Text size="sm" color="dimmed">
                        <FormattedMessage id="userInfo.pubKey" />
                      </Text>
                      <Button
                        size="xs"
                        variant="light"
                        onClick={copyPublicKey}
                        disabled={copySuccess}
                        compact
                      >
                        {copySuccess 
                          ? <FormattedMessage id="button.copy.success" /> 
                          : <FormattedMessage id="button.copy" />
                        }
                      </Button>
                    </Group>
                    <Text size="xs" style={{ 
                      fontFamily: 'monospace', 
                      wordBreak: 'break-all',
                      color: '#495057'
                    }}>
                      {userFullDetails?.PublicKey ? 
                        `${userFullDetails.PublicKey.substring(0, 40)}...` : 
                        'Not available'
                      }
                    </Text>
                  </Box>
                </Stack>
              </Paper>
            </Container>
          </Tabs.Panel>

          {/* Profile Presentation Tab */}
          <Tabs.Panel value="profilePresentation" pt="xs">
            <Grid gutter="md">
              {/* LEFT: Editing Sections */}
              <Grid.Col span={12} md={7}>
            {/* QR Code & Save Section */}
            <Paper p="md" mb="md" withBorder data-tour-id="pp-qr-save">
              <Stack align="center" spacing="md">
                <Flex direction="column" align="center">
                  {qrCodeUrl ? (
                    <>
                      <Image
                        src={qrCodeUrl}
                        alt="Profile QR Code"
                        width={120}
                        height={120}
                        withPlaceholder={false}
                      />
                      <Group spacing="xs" mt="sm" position="center">
                        <ActionIcon 
                          variant="light" 
                          color="blue" 
                          onClick={downloadQRCode}
                          title="Download QR Code"
                        >
                          <IconDownload size="1rem" />
                        </ActionIcon>
                        <ActionIcon 
                          variant="light" 
                          color="teal" 
                          onClick={copyProfileLink}
                          title="Copy Profile Link"
                        >
                          <IconLink size="1rem" />
                        </ActionIcon>
                      </Group>
                    </>
                  ) : (
                    <Stack align="center" spacing="xs">
                      <IconPhoto size={48} color="gray" />
                      <Text color="dimmed" size="sm">
                        <FormattedMessage id="profile.qrcode.notfound" defaultMessage="QR Code not generated yet" />
                      </Text>
                    </Stack>
                  )}
                </Flex>
                {/* Display mode toggle — shown above Save when custom template exists */}
                {hasCustomTemplate && (
                  <Paper p="sm" withBorder style={{ width: '100%' }}>
                    <Text size="xs" weight={600} mb="xs">
                      <FormattedMessage id="profile.preview.mode.label" defaultMessage="Preview Mode" />
                    </Text>
                    <SegmentedControl
                      fullWidth
                      size="sm"
                      value={previewMode}
                      onChange={(mode) => { setPreviewMode(mode); saveProfile(mode); }}
                      data={[
                        { value: 'form',   label: <FormattedMessage id="profile.preview.mode.form"   defaultMessage="Form Preview" /> },
                        { value: 'custom', label: <FormattedMessage id="profile.preview.mode.custom" defaultMessage="Custom HTML"  /> },
                      ]}
                    />
                    <Text size="xs" color="dimmed" mt={6}>
                      <FormattedMessage id="profile.preview.mode.hint" defaultMessage="This also controls what visitors see on your public profile." />
                    </Text>
                  </Paper>
                )}
                <Box style={{ width: '100%' }}>
                  <Switch
                    size="lg"
                    checked={published}
                    onChange={(e) => handlePublishToggle(e.currentTarget.checked)}
                    label={<FormattedMessage id="profile.publish.toggle" defaultMessage="Publish Profile" />}
                    onLabel={<FormattedMessage id="profile.publish.on" defaultMessage="ON" />}
                    offLabel={<FormattedMessage id="profile.publish.off" defaultMessage="OFF" />}
                    color="teal"
                  />
                  <Text size="xs" color="dimmed" mt={6}>
                    <FormattedMessage id="profile.publish.hint" defaultMessage="When enabled, your profile is publicly visible to anyone with the link." />
                  </Text>
                </Box>

                {/* Compact Status Indicators */}
                <Group spacing="sm" style={{ flexWrap: 'wrap' }}>
                  <Badge size="md" color={title ? "teal" : "red"} variant="dot">
                    <FormattedMessage id="profile.status.title" />
                  </Badge>
                  <Badge size="md" color={subtitle ? "teal" : "red"} variant="dot">
                    <FormattedMessage id="profile.status.subtitle" />
                  </Badge>
                  <Badge size="md" color={description ? "teal" : "red"} variant="dot">
                    <FormattedMessage id="profile.status.description" /> {description && `(${description.length})`}
                  </Badge>
                  <Badge size="md" color={infoGrid.filter(i => i.value).length > 0 ? "teal" : "red"} variant="dot">
                    <FormattedMessage id="profile.status.infogrid" /> {infoGrid.filter(i => i.value).length > 0 && `(${infoGrid.filter(i => i.value).length}/8)`}
                  </Badge>
                  <Badge size="md" color={location?.address ? "teal" : "red"} variant="dot">
                    <FormattedMessage id="profile.status.location" />
                  </Badge>
                  <Badge size="md" color={gallery.length > 0 ? "teal" : "red"} variant="dot">
                    <FormattedMessage id="profile.status.gallery" /> {gallery.length > 0 && `(${gallery.length})`}
                  </Badge>
                  <Badge size="md" color={productLots.length > 0 ? "teal" : "red"} variant="dot">
                    <FormattedMessage id="profile.status.lots" /> {productLots.length > 0 && `(${productLots.length})`}
                  </Badge>
                  <Badge size="md" color={contact?.name ? "teal" : "red"} variant="dot">
                    <FormattedMessage id="profile.status.contact" />
                  </Badge>
                  <Badge size="md" color={(socialLinks?.instagram || socialLinks?.twitter || socialLinks?.website) ? "teal" : "red"} variant="dot">
                    <FormattedMessage id="profile.status.social" />
                  </Badge>
                </Group>
              </Stack>
            </Paper>

            {/* Edit Sections - Accordion */}
            <Accordion multiple defaultValue={[]} styles={{
              content: { maxHeight: '500px', overflowY: 'auto' }
            }} data-tour-id="pp-edit-sections">
              
              {/* Basic Info */}
              <Accordion.Item value="basic">
                <Accordion.Control icon={<IconInfoCircle size="1rem" />}>
                  <FormattedMessage id="profile.section.basic" defaultMessage="Basic Information" />
                </Accordion.Control>
                <Accordion.Panel>
                  <Stack spacing="md">
                    <TextInput
                      label={<FormattedMessage id="profile.title.label" defaultMessage="Title" />}
                      placeholder={intl.formatMessage({ id: "profile.title.placeholder" })}
                      value={title}
                      onChange={(e) => setTitleWithSave(e.target.value)}
                    />
                    <TextInput
                      label={<FormattedMessage id="profile.subtitle.label" defaultMessage="Subtitle" />}
                      placeholder={intl.formatMessage({ id: "profile.subtitle.placeholder" })}
                      value={subtitle}
                      onChange={(e) => setSubtitleWithSave(e.target.value)}
                    />
                    <Select
                      label={<FormattedMessage id="profile.headerColor.label" defaultMessage="Header Color Theme" />}
                      value={headerColor}
                      onChange={setHeaderColorWithSave}
                      maxDropdownHeight={180}
                      withinPortal
                      dropdownPosition="bottom"
                      styles={{
                        dropdown: { maxHeight: '180px' }
                      }}
                      data={[
                        { value: 'brown', label: '🟤 Brown Coffee' },
                        { value: 'green', label: '🟢 Forest Green' },
                        { value: 'blue', label: '🔵 Ocean Blue' },
                        { value: 'orange', label: '🟠 Sunset Orange' },
                        { value: 'purple', label: '🟣 Royal Purple' },
                        { value: 'teal', label: '🔷 Tropical Teal' },
                        { value: 'red', label: '🔴 Wine Red' },
                        { value: 'slate', label: '⚫ Slate Gray' },
                      ]}
                    />
                    <Textarea
                      label={<FormattedMessage id="profile.description.label" defaultMessage="Description" />}
                      placeholder={intl.formatMessage({ id: "profile.description.placeholder" })}
                      value={description}
                      onChange={(e) => setDescriptionWithSave(e.target.value)}
                      minRows={4}
                    />
                    <FileInput
                      label="Background Wallpaper"
                      placeholder="Upload background image"
                      accept="image/*"
                      icon={<IconPhoto size="1rem" />}
                      onChange={handleBackgroundImageUpload}
                    />
                    <Text size="xs" color="dimmed">
                      Upload a subtle pattern or texture for the profile background
                    </Text>
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>

              {/* Hero Image */}
              <Accordion.Item value="hero">
                <Accordion.Control icon={<IconPhoto size="1rem" />}>
                  <FormattedMessage id="profile.section.hero" defaultMessage="Hero Image" />
                </Accordion.Control>
                <Accordion.Panel>
                  <Stack spacing="md">
                    {heroImage && (
                      <Image src={heroImage} alt="Hero" radius="md" style={{ maxHeight: '200px', objectFit: 'cover' }} />
                    )}
                    <TextInput
                      label={<FormattedMessage id="profile.heroImage.url" defaultMessage="Hero Image URL" />}
                      placeholder="https://..."
                      value={heroImage}
                      onChange={(e) => setHeroImageWithSave(e.target.value)}
                    />
                    <Divider label={<FormattedMessage id="profile.divider.or" defaultMessage="OR" />} labelPosition="center" />
                    <FileInput
                      label={<FormattedMessage id="profile.hero.upload" defaultMessage="Upload Hero Image" />}
                      placeholder={intl.formatMessage({ id: 'profile.hero.uploadPlaceholder', defaultMessage: 'Choose an image to upload' })}
                      accept="image/*"
                      icon={<IconPhoto size="1rem" />}
                      onChange={handleHeroImageUpload}
                    />
                    <Text size="xs" color="dimmed">
                      <FormattedMessage id="profile.hero.uploadHint" defaultMessage="Upload an image or enter a URL above" />
                    </Text>
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>

              {/* Info Grid */}
              <Accordion.Item value="infogrid">
                <Accordion.Control icon={<IconUsers size="1rem" />}>
                  <FormattedMessage id="profile.section.infogrid" defaultMessage="Information Grid" />
                </Accordion.Control>
                <Accordion.Panel>
                  <Stack spacing="sm">
                    {infoGrid.map((item, index) => (
                      <Group key={index} grow>
                        <TextInput
                          placeholder="Label"
                          value={item.label}
                          onChange={(e) => {
                            const updated = [...infoGrid];
                            updated[index].label = e.target.value;
                            setInfoGridWithSave(updated);
                          }}
                        />
                        <TextInput
                          placeholder="Value"
                          value={item.value}
                          onChange={(e) => {
                            const updated = [...infoGrid];
                            updated[index].value = e.target.value;
                            setInfoGridWithSave(updated);
                          }}
                        />
                      </Group>
                    ))}
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>

              {/* Location */}
              <Accordion.Item value="location">
                <Accordion.Control icon={<IconMap size="1rem" />}>
                  <FormattedMessage id="profile.section.location" defaultMessage="Location & Map" />
                </Accordion.Control>
                <Accordion.Panel>
                  <Stack spacing="md">
                    <TextInput
                      label={<FormattedMessage id="profile.location.address" defaultMessage="Address" />}
                      value={location.address}
                      onChange={(e) => setLocationWithSave({...location, address: e.target.value})}
                    />
                    <TextInput
                      label={<FormattedMessage id="profile.location.description" defaultMessage="Location Description" />}
                      value={location.description}
                      onChange={(e) => setLocationWithSave({...location, description: e.target.value})}
                    />
                    <TextInput
                      label={<FormattedMessage id="profile.location.mapUrl" defaultMessage="Google Maps Embed URL" />}
                      placeholder="https://www.google.com/maps/embed?pb=..."
                      value={location.mapUrl}
                      onChange={(e) => setLocationWithSave({...location, mapUrl: e.target.value})}
                    />
                    <TextInput
                      label={<FormattedMessage id="profile.coordinates.decimal" defaultMessage="Decimal Coordinates" />}
                      placeholder="1.386139,-77.174028"
                      value={location.coordinates}
                      onChange={(e) => setLocationWithSave({...location, coordinates: e.target.value})}
                    />
                    <TextInput
                      label={<FormattedMessage id="profile.coordinates.dms" defaultMessage="DMS Coordinates" />}
                      placeholder="1°23'10.1&quot;N 77°10'26.5&quot;W"
                      value={location.coordinatesDMS}
                      onChange={(e) => setLocationWithSave({...location, coordinatesDMS: e.target.value})}
                    />
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>

              {/* Gallery */}
              <Accordion.Item value="gallery">
                <Accordion.Control icon={<IconPhoto size="1rem" />}>
                  <FormattedMessage id="profile.section.gallery" defaultMessage="Image Gallery" />
                </Accordion.Control>
                <Accordion.Panel>
                  <Stack spacing="sm">
                    {gallery.map((url, index) => (
                      <Group key={index}>
                        <TextInput
                          style={{ flex: 1 }}
                          placeholder="Image URL"
                          value={url}
                          onChange={(e) => {
                            const updated = [...gallery];
                            updated[index] = e.target.value;
                            setGalleryWithSave(updated);
                          }}
                        />
                        <ActionIcon color="red" onClick={() => {
                          const updated = gallery.filter((_, i) => i !== index);
                          setGalleryWithSave(updated);
                        }}>
                          <IconX size="1rem" />
                        </ActionIcon>
                      </Group>
                    ))}
                    <Group grow>
                      <Button
                        variant="light"
                        leftIcon={<IconPlus size="1rem" />}
                        onClick={() => setGalleryWithSave([...gallery, ''])}
                      >
                        <FormattedMessage id="profile.gallery.addUrl" defaultMessage="Add Image URL" />
                      </Button>
                      <FileButton onChange={handleGalleryImageUpload} accept="image/*">
                        {(props) => <Button {...props} variant="filled" leftIcon={<IconPhoto size="1rem" />}><FormattedMessage id="profile.gallery.upload" defaultMessage="Upload Image" /></Button>}
                      </FileButton>
                    </Group>
                    <Text size="xs" color="dimmed">
                      <FormattedMessage id="profile.gallery.uploadHint" defaultMessage="Add images by entering URLs or uploading from your device" />
                    </Text>
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>

              {/* Product Lots */}
              <Accordion.Item value="lots">
                <Accordion.Control icon={<IconShoppingCart size="1rem" />}>
                  <FormattedMessage id="profile.section.lots" defaultMessage="Product Lots" />
                </Accordion.Control>
                <Accordion.Panel>
                  <Stack spacing="sm">
                    {productLots.map((lot, index) => (
                      <Paper key={index} p="sm" withBorder>
                        <Stack spacing="xs">
                          <Group>
                            <TextInput
                              style={{ flex: 1 }}
                              placeholder="Code (e.g., GGL01)"
                              value={lot.code}
                              onChange={(e) => {
                                const updated = [...productLots];
                                updated[index].code = e.target.value;
                                setProductLotsWithSave(updated);
                              }}
                            />
                            <ActionIcon color="red" onClick={() => {
                              const updated = productLots.filter((_, i) => i !== index);
                              setProductLotsWithSave(updated);
                            }}>
                              <IconX size="1rem" />
                            </ActionIcon>
                          </Group>
                          <TextInput
                            placeholder="Name"
                            value={lot.name}
                            onChange={(e) => {
                              const updated = [...productLots];
                              updated[index].name = e.target.value;
                              setProductLotsWithSave(updated);
                            }}
                          />
                          <TextInput
                            placeholder="Certificate URL"
                            value={lot.certificateUrl}
                            onChange={(e) => {
                              const updated = [...productLots];
                              updated[index].certificateUrl = e.target.value;
                              setProductLotsWithSave(updated);
                            }}
                          />
                        </Stack>
                      </Paper>
                    ))}
                    <Button
                      variant="light"
                      leftIcon={<IconPlus size="1rem" />}
                      onClick={() => setProductLotsWithSave([...productLots, { code: '', name: '', certificateUrl: '' }])}
                    >
                      <FormattedMessage id="profile.lots.add" defaultMessage="Add Product Lot" />
                    </Button>
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>

              {/* Contact */}
              <Accordion.Item value="contact">
                <Accordion.Control icon={<IconUsers size="1rem" />}>
                  <FormattedMessage id="profile.section.contact" defaultMessage="Contact Information" />
                </Accordion.Control>
                <Accordion.Panel>
                  <Stack spacing="md">
                    <TextInput
                      label={<FormattedMessage id="profile.contact.name" defaultMessage="Contact Name" />}
                      value={contact.name}
                      onChange={(e) => setContactWithSave({...contact, name: e.target.value})}
                    />
                    <TextInput
                      label={<FormattedMessage id="profile.contact.phone" defaultMessage="Phone" />}
                      value={contact.phone}
                      onChange={(e) => setContactWithSave({...contact, phone: e.target.value})}
                    />
                    <TextInput
                      label="Email"
                      value={contact.email}
                      onChange={(e) => setContactWithSave({...contact, email: e.target.value})}
                    />
                    <TextInput
                      label={<FormattedMessage id="profile.contact.address" defaultMessage="Address" />}
                      value={contact.address}
                      onChange={(e) => setContactWithSave({...contact, address: e.target.value})}
                    />
                    
                    <Divider mt="md" mb="xs" label={<FormattedMessage id="profile.section.social" defaultMessage="Social Media & Website" />} />
                    
                    <TextInput
                      label={<FormattedMessage id="profile.social.instagram" defaultMessage="Instagram URL" />}
                      placeholder="https://instagram.com/yourprofile"
                      value={socialLinks.instagram}
                      onChange={(e) => setSocialLinksWithSave({...socialLinks, instagram: e.target.value})}
                    />
                    <TextInput
                      label={<FormattedMessage id="profile.social.twitter" defaultMessage="X (Twitter) URL" />}
                      placeholder="https://x.com/yourprofile"
                      value={socialLinks.twitter}
                      onChange={(e) => setSocialLinksWithSave({...socialLinks, twitter: e.target.value})}
                    />
                    <TextInput
                      label={<FormattedMessage id="profile.social.website" defaultMessage="Website URL" />}
                      placeholder="https://yourwebsite.com"
                      value={socialLinks.website}
                      onChange={(e) => setSocialLinksWithSave({...socialLinks, website: e.target.value})}
                    />
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>

              {/* Custom Links */}
              <Accordion.Item value="customlinks">
                <Accordion.Control icon={<IconLink size="1rem" />}>
                  <FormattedMessage id="profile.section.customlinks" defaultMessage="Custom Links" />
                </Accordion.Control>
                <Accordion.Panel>
                  <Stack spacing="md">
                    <Text size="sm" color="dimmed">
                      <FormattedMessage 
                        id="profile.customlinks.hint" 
                        defaultMessage="Add links to your online shop, external certifications, product labels, or any other relevant pages you want to share with visitors." 
                      />
                    </Text>
                    
                    {customLinks.map((link, index) => (
                      <Paper key={index} p="sm" withBorder>
                        <Stack spacing="xs">
                          <Group position="apart">
                            <Text size="sm" weight={500}>Link {index + 1}</Text>
                            <ActionIcon
                              color="red"
                              variant="subtle"
                              onClick={() => {
                                const newLinks = customLinks.filter((_, i) => i !== index);
                                setCustomLinksWithSave(newLinks);
                              }}
                            >
                              <IconTrash size="1rem" />
                            </ActionIcon>
                          </Group>
                          <TextInput
                            label={<FormattedMessage id="profile.customlink.title" defaultMessage="Link Title" />}
                            placeholder={intl.formatMessage({ 
                              id: "profile.customlink.title.placeholder",
                              defaultMessage: "e.g., Shop Website, Certification, Product Label"
                            })}
                            value={link.title || ''}
                            onChange={(e) => {
                              const newLinks = [...customLinks];
                              newLinks[index] = { ...link, title: e.target.value };
                              setCustomLinksWithSave(newLinks);
                            }}
                          />
                          <TextInput
                            label="URL"
                            placeholder="https://..."
                            value={link.url || ''}
                            onChange={(e) => {
                              const newLinks = [...customLinks];
                              newLinks[index] = { ...link, url: e.target.value };
                              setCustomLinksWithSave(newLinks);
                            }}
                          />
                        </Stack>
                      </Paper>
                    ))}
                    
                    <Button
                      variant="light"
                      leftIcon={<IconPlus size="1rem" />}
                      onClick={() => setCustomLinksWithSave([...customLinks, { title: '', url: '' }])}
                    >
                      <FormattedMessage id="profile.customlinks.add" defaultMessage="Add Custom Link" />
                    </Button>
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>
              </Grid.Col>

              {/* RIGHT: Preview — shows only what matches the selected mode */}
              <Grid.Col span={12} md={5}>
                {hasCustomTemplate && previewMode === 'custom' ? (
                  <Paper p="md" withBorder style={{ position: 'sticky', top: '20px' }} data-tour-id="pp-preview">
                    <Title order={3} mb="xs" align="center">
                      <FormattedMessage id="profile.preview.custom.title" defaultMessage="Custom Template Preview" />
                    </Title>
                    <Text size="xs" color="dimmed" mb="md" align="center">
                      <FormattedMessage id="profile.preview.custom.description" defaultMessage="This is how your custom ProfilePresentation.html will appear to visitors." />
                    </Text>
                    <Divider mb="md" />
                    <Box style={{ width: '100%', height: '500px', border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden' }}>
                      <iframe
                        src={`https://vertoledgerstorage.blob.core.windows.net/sqldbledgerdigests/userSpace/${accountId}/ProfilePresentation.html`}
                        style={{ width: '100%', height: '100%', border: 'none' }}
                        title="Custom Profile Presentation Preview"
                      />
                    </Box>
                  </Paper>
                ) : (
                <Paper p="md" withBorder data-tour-id="pp-preview" style={{ 
                  position: 'sticky', 
                  top: '20px', 
                  overflowY: 'auto',
                  backgroundImage: backgroundImage ? `url(${backgroundImage})` : 'none',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat'
                }}>
                  <Title order={3} mb="md" align="center">
                    <FormattedMessage id="profile.preview.title" defaultMessage="Live Preview" />
                  </Title>
                  <Divider mb="md" />
                  
                  {/* Preview Content */}
                  <Box style={{ fontSize: '0.85rem' }}>
                    {/* Hero Image Preview */}
                    {heroImage && (
                      <Box mb="sm" style={{ textAlign: 'center' }}>
                        <Image
                          src={heroImage}
                          alt="Hero"
                          height={120}
                          fit="cover"
                          radius="md"
                        />
                      </Box>
                    )}
                    
                    {/* Header Preview with Color Theme */}
                    {headerColor && (
                      <Box mb="sm" style={{
                        background: headerColor === 'brown' ? 'linear-gradient(135deg, #6B4423 0%, #3d2817 100%)' :
                                   headerColor === 'green' ? 'linear-gradient(135deg, #2d5016 0%, #1a3d0f 100%)' :
                                   headerColor === 'blue' ? 'linear-gradient(135deg, #1e3a8a 0%, #0c1d4d 100%)' :
                                   headerColor === 'orange' ? 'linear-gradient(135deg, #c2410c 0%, #7c2d12 100%)' :
                                   headerColor === 'purple' ? 'linear-gradient(135deg, #6b21a8 0%, #4c1d95 100%)' :
                                   headerColor === 'teal' ? 'linear-gradient(135deg, #0f766e 0%, #134e4a 100%)' :
                                   headerColor === 'red' ? 'linear-gradient(135deg, #991b1b 0%, #7f1d1d 100%)' :
                                   headerColor === 'slate' ? 'linear-gradient(135deg, #475569 0%, #1e293b 100%)' : 
                                   'linear-gradient(135deg, #6B4423 0%, #3d2817 100%)',
                        color: 'white',
                        padding: '2rem 1rem',
                        textAlign: 'center',
                        borderRadius: '8px'
                      }}>
                        {title && <Title order={4} mb={4} style={{ color: 'white' }}>{title}</Title>}
                        {subtitle && <Text size="sm" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>{subtitle}</Text>}
                        {!title && !subtitle && (
                          <Text size="sm" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                            {headerColor === 'brown' ? '🟤 Brown Coffee Theme' :
                             headerColor === 'green' ? '🟢 Forest Green Theme' :
                             headerColor === 'blue' ? '🔵 Ocean Blue Theme' :
                             headerColor === 'orange' ? '🟠 Sunset Orange Theme' :
                             headerColor === 'purple' ? '🟣 Royal Purple Theme' :
                             headerColor === 'teal' ? '🔷 Tropical Teal Theme' :
                             headerColor === 'red' ? '🔴 Wine Red Theme' :
                             headerColor === 'slate' ? '⚫ Slate Gray Theme' : 'Header Preview'}
                          </Text>
                        )}
                      </Box>
                    )}
                    
                    {/* Description */}
                    {description && (
                      <Paper p="sm" mb="sm" radius="md" style={{ background: '#f9f9f9' }}>
                        <Text size="xs" weight={600} mb={4}><FormattedMessage id="profile.about" defaultMessage="About" /></Text>
                        <Text size="xs" style={{ lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{description}</Text>
                      </Paper>
                    )}
                    
                    {/* Info Grid */}
                    {infoGrid.filter(i => i.value).length > 0 && (
                      <Paper p="sm" mb="sm" radius="md" style={{ background: '#f9f9f9' }}>
                        <Text size="xs" weight={600} mb={4}><FormattedMessage id="profile.section.infogrid" defaultMessage="Details" /></Text>
                        <Grid gutter={4}>
                          {infoGrid.filter(i => i.value).map((item, idx) => (
                            <Grid.Col span={6} key={idx}>
                              <Text size="xs" weight={500} color="dimmed">{item.label}</Text>
                              <Text size="xs">{item.value}</Text>
                            </Grid.Col>
                          ))}
                        </Grid>
                      </Paper>
                    )}
                    
                    {/* Location */}
                    {location?.address && (
                      <Paper p="sm" mb="sm" radius="md" style={{ background: '#f9f9f9' }}>
                        <Text size="xs" weight={600} mb={4}>📍 <FormattedMessage id="profile.location" defaultMessage="Location" /></Text>
                        <Text size="xs">{location.address}</Text>
                        {location.description && <Text size="xs" color="dimmed" mt={2}>{location.description}</Text>}
                      </Paper>
                    )}
                    
                    {/* Gallery */}
                    {gallery.length > 0 && (
                      <Paper p="sm" mb="sm" radius="md" style={{ background: '#f9f9f9' }}>
                        <Text size="xs" weight={600} mb={4}>🖼️ <FormattedMessage id="profile.status.gallery" defaultMessage="Gallery" /> ({gallery.length})</Text>
                        <Grid gutter={4}>
                          {gallery.slice(0, 3).map((img, idx) => (
                            <Grid.Col span={4} key={idx}>
                              <Image src={img} height={40} fit="cover" radius="sm" />
                            </Grid.Col>
                          ))}
                        </Grid>
                      </Paper>
                    )}
                    
                    {/* Product Lots */}
                    {productLots.length > 0 && (
                      <Paper p="sm" mb="sm" radius="md" style={{ background: '#f9f9f9' }}>
                        <Text size="xs" weight={600} mb={4}>📦 <FormattedMessage id="profile.section.lots" defaultMessage="Product Lots" /> ({productLots.length})</Text>
                        {productLots.slice(0, 2).map((lot, idx) => (
                          <Text size="xs" key={idx}>• {lot.name || lot.code}</Text>
                        ))}
                      </Paper>
                    )}
                    
                    {/* Contact */}
                    {contact?.name && (
                      <Paper p="sm" mb="sm" radius="md" style={{ background: '#f9f9f9' }}>
                        <Text size="xs" weight={600} mb={4}>📞 <FormattedMessage id="profile.status.contact" defaultMessage="Contact" /></Text>
                        <Text size="xs">{contact.name}</Text>
                        {contact.phone && <Text size="xs">{contact.phone}</Text>}
                        {contact.email && <Text size="xs">{contact.email}</Text>}
                      </Paper>
                    )}
                    
                    {/* Social Links */}
                    {(socialLinks?.instagram || socialLinks?.twitter || socialLinks?.website) && (
                      <Paper p="sm" mb="sm" radius="md" style={{ background: '#f9f9f9' }}>
                        <Text size="xs" weight={600} mb={4}>🔗 <FormattedMessage id="profile.status.social" defaultMessage="Social & Web" /></Text>
                        {socialLinks.instagram && <Text size="xs">📷 Instagram</Text>}
                        {socialLinks.twitter && <Text size="xs">🐦 X (Twitter)</Text>}
                        {socialLinks.website && <Text size="xs">🌐 Website</Text>}
                      </Paper>
                    )}

                    {/* Custom Links */}
                    {customLinks.filter(l => l.title || l.url).length > 0 && (
                      <Paper p="sm" radius="md" style={{ background: '#f9f9f9' }}>
                        <Text size="xs" weight={600} mb={4}>🔗 <FormattedMessage id="profile.section.customlinks" defaultMessage="Custom Links" /></Text>
                        {customLinks.filter(l => l.title || l.url).map((link, idx) => (
                          <Text size="xs" key={idx}>• {link.title || link.url}</Text>
                        ))}
                      </Paper>
                    )}
                  </Box>
                </Paper>
                )}
              </Grid.Col>
            </Grid>

          </Tabs.Panel>
          <Tabs.Panel value="files" pt="xs">
            {/* Upload Section */}
            <Center mb="md" data-tour-id="files-upload">
              <DropzoneButton onUploadSuccess={fetchFiles} />
            </Center>
            
            {/* Files List */}
            <Stack spacing="xs" data-tour-id="files-list">
              {files.map((file) => (
                <Paper 
                  key={file.name}
                  withBorder 
                  p="sm"
                  style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center' 
                  }}
                >
                  {/* Icon based on file type */}
                  {file.name.endsWith('.pdf') ? <IconPdf size="1.5rem" /> :
                  file.name.endsWith('.png') || file.name.endsWith('.jpg') ? <IconPhoto size="1.5rem" /> :
                  <IconFile size="1.5rem" />}
                  
                  <Text style={{ flex: 1, marginLeft: '10px' }}>{file.name}</Text>
                  
                  {/* Icon-only actions */}
                  <Group spacing="xs">
                    <ActionIcon 
                      variant="subtle" 
                      color="blue"
                      onClick={() => window.open(file.url, '_blank')}
                      title="View"
                    >
                      <IconEye size="1rem" />
                    </ActionIcon>
                    <ActionIcon 
                      variant="subtle" 
                      color="green"
                      onClick={() => handleDocumentDownload(file.url, file.name)}
                      title="Download"
                    >
                      <IconDownload size="1rem" />
                    </ActionIcon>
                    <ActionIcon 
                      variant="subtle" 
                      color="red"
                      onClick={() => handleDeleteFile(file.name)}
                      title="Delete"
                    >
                      <IconTrash size="1rem" />
                    </ActionIcon>
                  </Group>
                </Paper>
              ))}
            </Stack>
          </Tabs.Panel>
        </Tabs>

      {/* Personal Space Guided Tour */}
      <GuidedTour
        runTour={runTour}
        onTourEnd={() => setRunTour(false)}
        steps={personalSpaceTourSteps}
      />
    </Box>
  );
}